import { HttpClient } from "@angular/common/http";
import { EventEmitter, inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { UserGroup } from "src/app/common/models/user-group.model";
import { UserRole } from "src/app/common/services/user-roles.service";
import { PromptsService } from "src/app/modules/prompts/services/prompts.service";

const API_PATH = "/api/internal/acl";

@Injectable({
  providedIn: "root",
})
export class AclService {
  private activeRoles?: UserRole[];
  private activePromptRoles?: string[];

  changeAccessEvent = new EventEmitter<null>();
  private readonly httpClient = inject(HttpClient);
  private readonly promptsService = inject(PromptsService);

  async getGroups(): Promise<UserGroup[]> {
    return await firstValueFrom(this.httpClient.get<UserGroup[]>(`${API_PATH}/groups`));
  }

  async getGroup(groupId: string): Promise<UserGroup> {
    return await firstValueFrom(this.httpClient.get<UserGroup>(`${API_PATH}/groups/${groupId}`));
  }

  async isAuthorized(authRole: UserRole): Promise<boolean> {
    if (!this.activeRoles) {
      this.activeRoles = await firstValueFrom(this.httpClient.get<UserRole[]>(`${API_PATH}/currentuserroles`));
    }
    return this.activeRoles.includes(authRole);
  }

  async canAccessPrompt(promptId: string): Promise<boolean> {
    if (!this.activePromptRoles) {
      this.activePromptRoles = await firstValueFrom(
        this.httpClient.get<string[]>(`${API_PATH}/currentuserpromptroles`),
      );
    }
    return this.activePromptRoles.includes(promptId);
  }

  async toggleRole(groupId: string, role: UserRole): Promise<UserGroup> {
    const userGroup = await firstValueFrom(
      this.httpClient.post<UserGroup>(`${API_PATH}/groups/${groupId}/role/${role}`, {}),
    );
    this.activeRoles = await firstValueFrom(this.httpClient.get<UserRole[]>(`${API_PATH}/currentuserroles`));
    this.promptsService.predefinedPromptsChanged.emit();
    return userGroup;
  }

  async togglePromptRole(groupId: string, promptId: string): Promise<UserGroup> {
    const userGroup = await firstValueFrom(
      this.httpClient.post<UserGroup>(`${API_PATH}/groups/${groupId}/promptrole/${promptId}`, {}),
    );
    this.activePromptRoles = await firstValueFrom(this.httpClient.get<string[]>(`${API_PATH}/currentuserpromptroles`));
    this.promptsService.predefinedPromptsChanged.emit();
    return userGroup;
  }

  async deleteGroup(groupId: string): Promise<void> {
    await firstValueFrom(this.httpClient.delete(`${API_PATH}/groups/${groupId}`, {}));
  }

  async renameGroup(groupId: string, newAzureId: string, newName: string): Promise<UserGroup> {
    return await firstValueFrom(
      this.httpClient.post<UserGroup>(`${API_PATH}/groups/${groupId}/${newAzureId}/${newName}`, {}),
    );
  }

  async createGroupe(): Promise<UserGroup> {
    return await firstValueFrom(this.httpClient.post<UserGroup>(`${API_PATH}/groups/new`, {}));
  }
}
